import * as axios from 'axios';
import { AxiosError, AxiosResponse, Canceler } from 'axios';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import AxiosHelper, { CustomAxiosRequestConfig } from 'services/AxiosHelper';
import { EntityDefinition, IndexResults } from 'types';
import BasicDto from 'types/__models__/BasicDto';
import uuid from 'uuid/v4';
import { VisuallyHiddenAnnouncementTrigger } from 'VisuallyHiddenAnnouncement';
import { SortBy, SortByDirection } from '../../../../../Roc.SharedClientApp/types';
import Alert from '../Alert';
import { reorderArrayValues } from '../auto-suggest/Util';
import { toastError } from '../toast/Toasts';
import { Filter, SelectedFilter } from './filters';
import {
	getParsedQueryString,
	getQueryStringFromSearchQuery,
	getSearchQueryFromQueryString,
	getSearchQueryFromSavedFilter,
	QueryStringTarget,
	SearchQuery,
} from './filters/providers';
import { SavedFilter, SavedFilterCategory } from './saved-filters/SavedFilterPicker';
import { flattenSavedFilters } from './saved-filters/Util';

/**
 * Defines the different locations the listing components can be used from
 */
export enum ListingDisplayMode {
	/**
	 * Default. Used by all admin listings to display the main table with content.
	 */
	ListingPage,
	/**
	 * Used by the asset manager by default. This can be handy if you're displaying the table in a modal to make record selections.
	 * Disables some functionality related to redirects.
	 */
	SelectionModal,
	/**
	 * Used in Commerce embedded in the User admin to show related data". This disables additional functionality related to adding values to the Query String.
	 */
	EmbeddedInEditPage,
	/**
	 * Used within the Dashboard. Displays the table of results only.
	 */
	DashboardWidget,
}

const DEFAULT_PAGE_SIZE = 100;

export type ListingContainerProps = RouteComponentProps<any> & {
	entityDefinition: EntityDefinition;
	listingDisplayMode?: ListingDisplayMode;
	onRecordsSelectedChanged?: (selection: RecordSelectionState) => void;
	selectedRecords?: BasicDto[] | BulkEntity<BasicDto>;
	minimumSelectionCount?: number;
	maximumSelectionCount?: number;
	children: (props: ListingContainerContext<any>) => React.ReactNode;
	pageSize?: number;
	refreshTrigger?: number;
};

export interface BulkEntity<T> {
	entity: BulkRecords<T>;
}

export interface BulkRecords<T> {
	bulkRecords: T[];
}

export interface RecordSelectionState {
	recordIds: any;
	records?: any; // should only be set when listingDisplayMode is SelectionModal
	count: number;
	toggleRecordSelection: (record: BasicDto) => void;
	setRecordSelection: (record: BasicDto, state: boolean) => void;
	setFullPageSelection: (state: boolean) => void;
	clearSelection: () => void;
}

export enum FilterChangeLocation {
	FilterList = 'FilterList',
	SelectedFilter = 'SelectedFilter',
}

export interface FilterState {
	selectedFilters: SelectedFilter[];
	filterBeingChanged: SelectedFilter | null;
	displayFilterList: boolean;
	closeFilterList: () => void;
	toggleFilterList: () => void;
	toggleFilterChange: () => void;
	filterChangeLocation: FilterChangeLocation;
	beginFilterChange: (filter: Filter | SelectedFilter, location: FilterChangeLocation) => void;
	onFilterChange: (selectedFilter: SelectedFilter, stopEditing: boolean) => void;
	cancelFilterChange: () => void;
	addOrUpdateFilter: (selectedFilter: SelectedFilter) => void;
	removeFilter: (selectedFilter: SelectedFilter) => void;
	onFilterRemoved: (selectedFilter: SelectedFilter, stopEditing: boolean) => void;
	resetFilters: () => void;
}

export interface SavedFiltersState {
	isOpen: boolean;
	appliedSavedFilter: SavedFilter | null;
	isEditMode: boolean;
	filtersByCategory: SavedFilterCategory[];
	refreshing: boolean;
	toggleDisplay: (isOpen: boolean) => void;
	applySavedFilter: (params: ApplySavedFilterOptions) => void;
	beginEditAppliedSavedFilter: () => void;
	getSavedFilters: (options: GetSavedFiltersOptions) => void;
	deleteSavedFilter: (filter: SavedFilter) => void;
}

export interface GetSavedFiltersOptions {
	refresh: boolean;
}

export interface ApplySavedFilterOptions {
	filter: SavedFilter;
	isEditMode: boolean;
}

export interface SavedFilterResponse {
	categories: SavedFilterCategory[];
}

export interface CustomSortingState {
	/**
	 * Determines whether custom sorting mode is enabled or not.
	 */
	isEnabled: boolean;

	/**
	 * Toggles custom sorting mode on/off based on the state argument
	 */
	setCustomSorting: (state: boolean) => void;

	/**
	 * Handler for when a sorting change (usually a drag/drop) is applied, so that it can be persisted on the server
	 */
	onDragDropSortOrderUpdate: (sourceIndex: number, destinationIndex: number | null, newSortOrder: number) => void;
}

export interface ListingContainerState<TModel extends BasicDto> {
	results?: IndexResults<TModel>;
	isLoading: boolean;
	error: AxiosError | Error | null;
	pendingSearchQuery: string;
	searchQuery: string;
	pendingPageChange: number;
	sortBy?: SortBy;
	handleBasicSearchChange: (basicSearch: string) => void;
	resetSearch: () => void;

	handleDelete: (record: any, nameField: any) => void;
	handlePageChange: (page: number) => Promise<void>;
	executePageChange: () => Promise<void>;
	refreshData: () => void;
	executeSearch: () => Promise<void>;
	setSearchSortBy: (sortBy: SortBy) => void;

	selection: RecordSelectionState;
	filters: FilterState;
	savedFilters: SavedFiltersState;
	isHierarchy: boolean;
	isAlertOpen: boolean;
	alertMessage: string;

	pageChangeAlert: string | null;

	customSorting: CustomSortingState;
}

// not really context, but it will be eventually.
export interface ListingContainerContext<TModel extends BasicDto> extends ListingContainerState<TModel> {
	listingDisplayMode: ListingDisplayMode;
}

/**
 * Top level container component for all listing pages. This component is in charge of managing all
 * of the state needed by the listing page controls, and uses render props to render its children
 *
 * @class ListingContainer
 * @extends {React.Component<ListingContainerProps, ListingContainerState<any>>}
 */
class ListingContainer extends React.Component<ListingContainerProps, ListingContainerState<any>> {
	public static defaultProps = {
		listingDisplayMode: ListingDisplayMode.ListingPage,
		minimumSelectionCount: 0,
		maximumSelectionCount: 1,
		onRecordsSelectedChanged: () => {},
		pageSize: DEFAULT_PAGE_SIZE,
		refreshTrigger: 0,
	};

	private mounted: boolean = false;

	constructor(props) {
		super(props);
		const { entityDefinition } = this.props;

		this.state = {
			error: null,
			isHierarchy: entityDefinition.isHierarchy,
			results: {
				records: [],
				page: 1,
				pageSize: DEFAULT_PAGE_SIZE,
				totalRecords: 0,
				totalPages: 0,
				basicSearchFields: [],
				sortBys: [],
				parentRecords: null,
				appliedSortBy: {
					name: 'ModifyDate',
					title: 'Last Modified on top',
					direction: SortByDirection.Descending,
				},
				uniqueId: '',
				appliedSavedFilterId: '',
			},
			selection: {
				recordIds: {},
				records: [],
				count: 0,
				toggleRecordSelection: this.toggleRecordSelection,
				setRecordSelection: this.setRecordSelection,
				setFullPageSelection: this.setFullPageSelection,
				clearSelection: this.clearSelection,
			},
			filters: {
				selectedFilters: entityDefinition.filters.filter((f) => f.permanent) as SelectedFilter[],
				displayFilterList: false,
				closeFilterList: this.closeFilterList,
				filterBeingChanged: null,
				toggleFilterList: this.toggleFilterList,
				toggleFilterChange: this.toggleFilterChange,
				beginFilterChange: this.beginFilterChange,
				filterChangeLocation: FilterChangeLocation.FilterList,
				cancelFilterChange: this.cancelAddFilter,
				onFilterChange: this.onFilterChange,
				onFilterRemoved: this.onFilterRemoved,
				addOrUpdateFilter: this.addOrUpdateFilter,
				removeFilter: this.removeFilter,
				resetFilters: this.resetFilters,
			},
			savedFilters: {
				isOpen: false,
				toggleDisplay: this.toggleSavedFilterDisplay,
				applySavedFilter: this.applySavedFilter,
				appliedSavedFilter: null,
				isEditMode: false,
				beginEditAppliedSavedFilter: this.beginEditAppliedSavedFilter,
				getSavedFilters: this.getSavedFilters,
				deleteSavedFilter: this.deleteSavedFilter,
				refreshing: false,
				filtersByCategory: [],
			},
			customSorting: {
				isEnabled: false,
				onDragDropSortOrderUpdate: this.onDragDropSortOrderUpdate,
				setCustomSorting: this.setCustomSorting,
			},
			isLoading: false,
			pendingSearchQuery: '',
			searchQuery: '',
			pendingPageChange: 1,
			sortBy: undefined,
			handleBasicSearchChange: this.handleBasicSearchChange,
			resetSearch: this.resetSearch,
			handleDelete: this.handleDelete,
			handlePageChange: this.handlePageChange,
			executePageChange: this.executePageChange,
			executeSearch: this.executeSearch,
			refreshData: this.refreshData,
			setSearchSortBy: this.setSearchSortBy,
			isAlertOpen: false,
			alertMessage: '',
			pageChangeAlert: null,
		};
	}

	componentDidUpdate(prevProps) {
		if (prevProps.refreshTrigger !== this.props.refreshTrigger) {
			this.refreshData();
		}
	}

	/**
	 * Allows calling setState with async/await
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private setStateAsync = (state: any): Promise<void> => {
		return new Promise((resolve) => {
			this.setState(state, resolve);
		});
	};

	/**
	 * Deletes a saved filter, and optimistically updates state for a better user experience.
	 * If the filter being deleted is the active one, this will remove the filter association.
	 * If something goes wrong and the optimistic update was wrong, we simply reload all values into state.
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private deleteSavedFilter = async (filter: SavedFilter) => {
		console.debug('Deleting filter:', filter.name);

		const { entityDefinition } = this.props;
		const { savedFilters } = this.state;

		this.setState((prevState) => {
			const filtersByCategory: SavedFilterCategory[] = prevState.savedFilters.filtersByCategory.map(
				(filterCategory) => {
					return {
						...filterCategory,
						savedFilters: filterCategory.savedFilters.filter(
							(savedFilter: SavedFilter) => savedFilter.id !== filter.id,
						),
					};
				},
			);

			return {
				savedFilters: { ...prevState.savedFilters, filtersByCategory },
			};
		});

		try {
			// optimistically update UI so we no longer have the selected filter active.
			if (savedFilters.appliedSavedFilter !== null && savedFilters.appliedSavedFilter.id === filter.id) {
				this.setState(
					(prevState: any) => {
						return {
							savedFilters: {
								...prevState.savedFilters,
								appliedSavedFilter: null,
							},
							results: { ...prevState.results, appliedSavedFilterId: '' },
						};
					},
					() => this.refreshData(),
				);
			}

			await AxiosHelper.delete(entityDefinition.baseAjaxRoute + '/filter/' + filter.id, { data: {} });
		} catch (error) {
			console.error(error);

			// if something goes wrong, reload all saved filters, as our optimistic update was wrong.
			this.getSavedFilters({ refresh: true });
		}
	};

	/**
	 * Shared promise used to allow multiple calls to getSavedFilters to be reduced to a single one.
	 *
	 * @private
	 * @type {Promise<SavedFilterResponse>}
	 * @memberof ListingContainer
	 */
	private refreshSavedFiltersPromise: Promise<SavedFilterResponse>;

	/**
	 * Returns all saved filters from the server and returns a promise with the results.
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private getSavedFilters = async ({ refresh = false }: GetSavedFiltersOptions): Promise<SavedFilterResponse> => {
		if (!this.refreshSavedFiltersPromise || refresh) {
			this.refreshSavedFiltersPromise = new Promise(async (resolve, reject) => {
				this.setState((prevState) => ({
					savedFilters: {
						...prevState.savedFilters,
						refreshing: true,
					},
				}));

				try {
					const { entityDefinition } = this.props;
					console.debug('Refreshing Saved Filters...');

					const response: AxiosResponse<SavedFilterResponse> = await AxiosHelper.get<SavedFilterResponse>(
						entityDefinition.baseAjaxRoute + '/filter',
					);

					const { data } = response;

					for (const savedFilterCategory of data.categories) {
						for (const savedFilter of savedFilterCategory.savedFilters) {
							savedFilter.categoryId = savedFilterCategory.id;
						}
					}

					resolve(data);

					// prevent attempting to setState after the component has unmounted.
					if (!this.mounted) {
						reject();
						return;
					}

					this.setState((prevState) => ({
						savedFilters: {
							...prevState.savedFilters,
							filtersByCategory: data.categories,
							refreshing: false,
						},
					}));
				} catch (error) {
					console.error(error);
					// TODO: display something in UI
					reject(error);

					this.setState((prevState) => ({
						savedFilters: {
							...prevState.savedFilters,
							refreshing: false,
						},
					}));
				}
			});
		}

		return this.refreshSavedFiltersPromise;
	};

	/**
	 * Display saved filters
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private toggleSavedFilterDisplay = (isOpen: boolean) => {
		this.setState((prevState) => ({
			savedFilters: { ...prevState.savedFilters, isOpen },
		}));
	};

	/**
	 * Sets a selected filter as the currently applied filter. This is used to display which filter
	 * is currently selected, helps keep the QueryString updated with savedFilterId, etc.
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private applySavedFilter = ({ filter, isEditMode = false }) => {
		console.debug('Applying Saved Filter:', filter);
		const search = getSearchQueryFromSavedFilter(filter, this.props.entityDefinition);

		this.setState(
			(prevState: any) => ({
				searchQuery: '',
				pendingSearchQuery: '',
				filters: { ...prevState.filters, selectedFilters: search.filters },
				savedFilters: { ...prevState.savedFilters, isOpen: false, appliedSavedFilter: filter, isEditMode },
				results: { ...prevState.results, page: 1, appliedSavedFilterId: !isEditMode ? filter.id : '' },
			}),
			() => {
				this.refreshData();
			},
		);
	};

	/**
	 * Applies a selected filter and triggers edit mode.
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private beginEditAppliedSavedFilter = () => {
		this.applySavedFilter({ filter: this.state.savedFilters.appliedSavedFilter, isEditMode: true });
	};

	/**
	 * Sets the state of whether a specific record in the listing is selected or not
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private toggleRecordSelection = (record: BasicDto) => {
		if (!record || !record.id) {
			console.warn('A record ID must be provided to toggle a selection.');
			return;
		}

		const existingRecordIndex = this.state.selection.records.findIndex((r) => r.id === record.id);
		if (existingRecordIndex < 0) {
			// does not exist already, set to true
			this.setRecordSelection(record, true);
		} else {
			const existingRecordSelected = this.state.selection.records[existingRecordIndex];
			this.setRecordSelection(record, !existingRecordSelected);
		}
	};

	/**
	 * Open reusable alert
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private openAlert = (message: string) => {
		this.setState({ isAlertOpen: true, alertMessage: message });
	};

	/**
	 * Close the reusable alert
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private closeAlert = () => {
		this.setState({ isAlertOpen: false, alertMessage: '' });
	};

	/**
	 * Sets the state of whether a specific record in the listing is selected or not
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private setRecordSelection = async (record: BasicDto, state: boolean) => {
		if (!record || !record.id) {
			console.warn('A record ID must be provided to set a selection.');
			return;
		}

		const maxSelections = this.props.maximumSelectionCount === undefined ? 1 : this.props.maximumSelectionCount;

		if (this.props.listingDisplayMode === ListingDisplayMode.SelectionModal && state === true) {
			if (maxSelections === 1 && this.state.selection.count > 0) {
				await this.clearSelection();
			} else if (maxSelections !== 0 && this.state.selection.count >= maxSelections) {
				const message =
					'You can only select up to ' +
					maxSelections +
					' record' +
					(maxSelections === 1 ? '' : 's') +
					' at a time.';

				this.openAlert(message);
				return;
			}
		}

		this.setState((prevState: ListingContainerState<any>) => {
			const recordIds = { ...prevState.selection.recordIds, [record.id]: state };

			const count = this.getSelectionCount(recordIds);

			console.debug('Selection changed:', count);

			const selection = {
				...prevState.selection,
				count,
				recordIds,
			};

			if (this.props.listingDisplayMode === ListingDisplayMode.SelectionModal) {
				const existingRecordIndex = prevState.selection.records.findIndex((r) => r.id === record.id);
				if (state && existingRecordIndex < 0) {
					// only add the record to the array if it doesn't already exist
					selection.records.push(record);
				} else if (!state) {
					selection.records.splice(existingRecordIndex, 1);
				}
			}

			if (this.props.onRecordsSelectedChanged) {
				this.props.onRecordsSelectedChanged(selection);
			}

			return {
				selection,
			};
		});
	};

	/**
	 * Adds all records from the current page of results to the list of selections.
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private setFullPageSelection = (state: boolean) => {
		this.setState((prevState: ListingContainerState<any>) => {
			if (prevState.results) {
				const fullPageSelection = prevState.results.records.reduce((acc, value) => {
					acc[value.id] = state;
					return acc;
				}, {});

				const recordIds = { ...prevState.selection.recordIds, ...fullPageSelection };
				const count = this.getSelectionCount(recordIds);

				console.debug('Selection changed:', count);

				const selection = {
					...prevState.selection,
					count,
					recordIds,
				};

				if (this.props.onRecordsSelectedChanged) {
					this.props.onRecordsSelectedChanged(selection);
				}

				return {
					selection,
				};
			}

			return null;
		});
	};

	/**
	 * Helper method to return the total number of selected records.
	 *
	 * @private
	 * @param {*} recordIds
	 * @returns
	 * @memberof ListingContainer
	 */
	private getSelectionCount(recordIds: any) {
		return Object.keys(recordIds).reduce((acc, value) => {
			if (recordIds[value]) {
				return acc + 1;
			}

			return acc;
		}, 0);
	}

	/**
	 * Completely removes all records from the current selection and resets the count.
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private clearSelection = () => {
		return this.setStateAsync((prevState: ListingContainerState<any>) => {
			const selection = {
				...prevState.selection,
				recordIds: {},
				records: [],
				count: 0,
			};

			if (this.props.onRecordsSelectedChanged) {
				this.props.onRecordsSelectedChanged(selection);
			}

			return {
				selection,
			};
		});
	};

	/**
	 * Maintains a reference to the history listener so that we can stop listening to these changes
	 * once the component is unmounted, to prevent multiple listeners
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private stopListeningToHistory: () => void;

	/**
	 * Load data as soon as the component is mounted
	 *
	 * @memberof ListingContainer
	 */
	public componentDidMount() {
		// avoid history updates if we're not in the listing page
		if (this.props.listingDisplayMode === ListingDisplayMode.ListingPage) {
			this.stopListeningToHistory =
				this.props.history &&
				this.props.history.listen((historyLocation, action) => {
					// a POP occurs when you hit "back" or "forward"
					if (action === 'POP') {
						console.debug('History Pop:', historyLocation);
						const search = historyLocation.search ? historyLocation.search.substring(1) : '';
						// loc.search starts with a '?' to let's remove that.
						this.refreshData(search, true, true);
					}
				});
		}

		this.mounted = true;

		this.getSavedFilters({ refresh: true });

		this.refreshData(location.search);

		// if the selectedRecords were passed into the component,
		// add them to the record selection so the user can see what is already selected
		const selectedRecords = this.props.selectedRecords;
		if (selectedRecords) {
			//Handle bulk records
			if ((selectedRecords as BulkEntity<BasicDto>).entity) {
				(selectedRecords as BulkEntity<BasicDto>).entity.bulkRecords.forEach((record) => {
					this.setRecordSelection(record, true);
				});
			} else {
				(selectedRecords as BasicDto[]).forEach((record) => {
					this.setRecordSelection(record, true);
				});
			}
		}
	}

	public componentWillUnmount() {
		this.mounted = false;

		if (this.stopListeningToHistory) {
			this.stopListeningToHistory();
		}

		// if the user is leaving the page and there's a pending query, cancel it.
		if (this.refreshDataCancelToken) {
			this.refreshDataCancelToken();
		}
	}

	private refreshDataCancelToken: Canceler | null = null;

	/**
	 * Calls the server to get results based on its state. Page/Search/etc are stored in state
	 * and modified from the different update functions
	 *
	 * @memberof ListingContainer
	 */
	public refreshData = async (
		queryString: string = '',
		forceUpdateFromQueryString: boolean = false,
		poppedFromHistory: boolean = false,
	): Promise<void> => {
		const {
			results,
			searchQuery,
			filters: { selectedFilters },
			sortBy,
			savedFilters,
			customSorting,
		} = this.state;

		const { entityDefinition } = this.props;

		if (!results) {
			return;
		}

		console.debug('Generating search query:', queryString);

		let serverQueryString = '';
		let clientQueryString = '';

		let search: SearchQuery;

		const parsedQueryString = getParsedQueryString(queryString);
		const savedFilterIdFromQueryString = parsedQueryString.savedFilterId;

		try {
			// if there's a pending request, cancel it to prevent having multiple ajax responses
			// coming back at the same time, which can cause weird UI quirks.
			if (this.refreshDataCancelToken) {
				this.refreshDataCancelToken();
			}

			// saved filter applied on server.
			if (savedFilterIdFromQueryString) {
				console.debug('Applying Saved Filter:', savedFilterIdFromQueryString);

				const savedFiltersResponse = await this.getSavedFilters({ refresh: false });
				const filters = flattenSavedFilters(savedFiltersResponse.categories);
				const filterMatch = filters.find((filter) => filter.id === savedFilterIdFromQueryString);

				if (filterMatch) {
					console.debug('Applying filter:', filterMatch);

					const savedFilterSearch = getSearchQueryFromSavedFilter(filterMatch, this.props.entityDefinition);

					await this.setStateAsync((prevState) => ({
						filters: {
							...prevState.filters,
							selectedFilters: savedFilterSearch.filters,
						},
						savedFilters: {
							...prevState.savedFilters,
							appliedSavedFilter: { ...filterMatch },
							isEditMode: false,
						},
					}));
				} else {
					// this should never happen, so it'll happen all the time, probably.
					console.warn('Unable to match applied filter.');
				}
			}

			// this is a safe conversion because of FilterKind, TypeScript just doesn't know that only implicit: true elements will be in the result.
			const implicitFilters = entityDefinition.filters.filter((f) => f.implicit) as SelectedFilter[];

			// if this is the first time the listing is loaded and there's something in the query string
			if (queryString || forceUpdateFromQueryString) {
				console.debug('Found QueryString override, force:', forceUpdateFromQueryString);

				search = getSearchQueryFromQueryString(queryString, entityDefinition);
				if (entityDefinition.hideSearch) {
					search = { ...search, q: '' };
				}

				// state may have already been updated with filters from a saved filter.
				// we can assume it's a saved filter because there's a query string set (?savedFilterId=<guid>).
				if (this.state.filters.selectedFilters.length > 0 && queryString !== '' && search.savedFilterId) {
					search = {
						...search,
						filters: [...this.state.filters.selectedFilters.concat(implicitFilters)],
					};
				}

				// If the user hits the Back button, we don't want to add custom sorting to the query string based on current state.
				// However, in other cases we want the new URL to reflect the current state
				const addCustomSortQuery = poppedFromHistory ? search.customSortingEnabled : customSorting.isEnabled;

				this.setState((prevState) => ({
					searchQuery: search.q,
					pendingSearchQuery: search.q,
					filters: { ...prevState.filters, selectedFilters: search.filters },
					customSorting: {
						...prevState.customSorting,
						isEnabled: search.customSortingEnabled ? true : false,
					},
				}));

				if (addCustomSortQuery) {
					search.customSortingEnabled = true;
				}

				// if there's no saved filter associated with this search, ensure that we aren't
				// keeping this in state, and that we are not in edit mode.
				if (!search.savedFilterId) {
					this.setState((prevState) => ({
						savedFilters: { ...prevState.savedFilters, isEditMode: false, appliedSavedFilter: null },
					}));
				}

				// override params to be sent to server with validated value from query string.
				serverQueryString = getQueryStringFromSearchQuery(search, QueryStringTarget.Server);
				clientQueryString = getQueryStringFromSearchQuery(search, QueryStringTarget.Client);

				console.debug('Server Query:', serverQueryString);
				console.debug('Client Query:', clientQueryString);

				// update url query string, except when in selection mode
				if (
					(clientQueryString || clientQueryString !== location.search) &&
					this.props.listingDisplayMode === ListingDisplayMode.ListingPage
				) {
					console.debug('Replacing Location History due to changes in QueryString:', clientQueryString);
					this.props.history.replace({ search: clientQueryString });
				}
			} else {
				console.debug('Creating search query from state');
				// a search is being executed due to a change in state
				search = {
					page: results.page,
					pageSize: results.pageSize,
					sortBy: sortBy && sortBy.name,
					sortDirection: sortBy && sortBy.direction,
					q: !entityDefinition.hideSearch ? searchQuery : '',
					savedFilterId: !savedFilters.isEditMode ? results.appliedSavedFilterId : '',
					customSortingEnabled: customSorting.isEnabled,
					filters: selectedFilters.concat(implicitFilters),
				};

				serverQueryString = getQueryStringFromSearchQuery(search, QueryStringTarget.Server);
				clientQueryString = getQueryStringFromSearchQuery(search, QueryStringTarget.Client);

				// update url query string, except when in selection mode
				if (
					(clientQueryString || clientQueryString !== location.search) &&
					this.props.listingDisplayMode === ListingDisplayMode.ListingPage
				) {
					console.debug('Adding Location History:', clientQueryString);
					this.props.history.push({ search: clientQueryString });
				}
			}

			let url = entityDefinition.baseAjaxRoute + serverQueryString;

			if (this.props.pageSize !== DEFAULT_PAGE_SIZE) {
				url += (serverQueryString ? '&' : '?') + `pageSize=${this.props.pageSize}`;
			}

			console.debug('Fetching ' + entityDefinition.pluralName, url);

			this.setState({
				isLoading: true,
			});

			const response: AxiosResponse<IndexResults<any>> = await AxiosHelper.get<IndexResults<any>>(url, {
				cancelToken: new axios.default.CancelToken((token) => {
					this.refreshDataCancelToken = token;
				}),
				redirectToAccessDeniedOn403: true,
			} as CustomAxiosRequestConfig);

			console.debug('Loaded ' + entityDefinition.pluralName);

			const { page, totalRecords } = response.data;

			if (response.data && response.data.page > 1 && response.data.records.length === 0) {
				this.handlePageChange(1);
				await this.executePageChange();
				return;
			}

			// for a11y, we want to give users an update of the change on the screen.
			const isFirstQuery = this.state.pageChangeAlert === null;

			let resultsCount: number | null = null;

			if (results) {
				resultsCount = totalRecords;
			}

			let pageChangeAlert = '';

			// the update includes details on the number of results as well as if there's a filter present.
			const hasFilter = this.state.searchQuery || this.state.filters.selectedFilters.length > 0;
			const selectionCount = this.state.selection.count === undefined ? 0 : this.state.selection.count;
			const resultsMessage = `${resultsCount} ${hasFilter ? 'filtered' : ''} result${
				resultsCount !== 1 ? 's' : ''
			} ${selectionCount > 0 ? `${selectionCount} selected` : ''}`;

			if (isFirstQuery) {
				pageChangeAlert =
					resultsCount !== null
						? `Navigated to ${
								entityDefinition.pageTitleOverride || entityDefinition.pluralName
						  }. ${resultsMessage}`
						: '';
			} else {
				pageChangeAlert = resultsCount !== null ? resultsMessage : '';
			}

			this.setState(() => ({
				pendingPageChange: page,
				results: response.data,
				pageChangeAlert,
			}));
		} catch (error) {
			if (axios.default.isCancel(error)) {
				console.warn('Refresh Data request canceled.');
				return;
			}

			const axiosError = error as axios.AxiosError;

			console.error('Unable to load', entityDefinition.pluralName);
			console.error(error);

			this.setState({
				results: undefined,
				error: axiosError,
			});
		} finally {
			this.refreshDataCancelToken = null;
		}

		this.setState({
			isLoading: false,
		});
	};

	/**
	 * Sets the sorting options for the listing page context and reloads data.
	 *
	 * @memberof ListingContainer
	 */
	public setSearchSortBy = (sortBy: SortBy): void => {
		this.setState((prevState: any) => {
			return { sortBy, results: { ...prevState.results, page: 1 } };
		}, this.refreshData);
	};

	/**
	 * Updates search query state when the basic search field is modified.
	 *
	 * @memberof ListingContainer
	 */
	public handleBasicSearchChange = (basicSearch: string) => {
		this.setState({
			pendingSearchQuery: basicSearch,
		});
	};

	/**
	 * Sets the page to whatever was selected from pagination
	 *
	 * @memberof ListingContainer
	 */
	public handlePageChange = (page: number): Promise<void> => {
		return this.setStateAsync({
			pendingPageChange: page,
		});
	};

	/**
	 * Sets the page to whatever was selected from pagination
	 *
	 * @memberof ListingContainer
	 */
	public executePageChange = async (): Promise<void> => {
		await this.setStateAsync((prevState: any) => ({
			results: { ...prevState.results, page: prevState.pendingPageChange },
		}));

		await this.refreshData();
	};

	/**
	 * Executes a simple search based on the query typed in by the user
	 *
	 * @memberof ListingContainer
	 */
	public executeSearch = async (): Promise<void> => {
		await this.setStateAsync((prevState: any) => {
			return {
				searchQuery: prevState.pendingSearchQuery,
				results: { ...prevState.results, page: 1 },
			};
		});

		await this.refreshData();
	};

	/**
	 * Completely resets search on the current listing
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private resetSearch = () => {
		this.setState(
			{
				searchQuery: '',
				pendingSearchQuery: '',
			},
			this.executeSearch,
		);
	};

	/**
	 * Resets current filters
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private resetFilters = () => {
		this.setState((prevState: any) => {
			const previousStateSelectedFilters = prevState.filters.selectedFilters as SelectedFilter[];
			return {
				filters: {
					...prevState.filters,
					selectedFilters: previousStateSelectedFilters.filter((f) => f.permanent),
				},
				savedFilters: { ...prevState.savedFilters, appliedSavedFilter: null, isEditMode: false },
				results: { ...prevState.results, appliedSavedFilterId: '' },
			};
		}, this.executeSearch);
	};

	/**
	 * Handles deleting entities from the listing pages.
	 *
	 * @memberof ListingContainer
	 */
	public handleDelete = async (record, nameField): Promise<void> => {
		let recordName: string = '';
		if (nameField && typeof record[nameField] === 'string') {
			recordName = ` (${record[nameField]})`;
		}

		if (confirm(`Are you sure you want to delete this ${this.props.entityDefinition.singularName}${recordName}?`)) {
			const url = `${this.props.entityDefinition.baseAjaxRoute}/${record.id}`;

			try {
				await AxiosHelper.delete(url);

				// prevent attempting to setState after the component has unmounted.
				if (!this.mounted) {
					return;
				}

				this.refreshData();
			} catch (error) {
				console.error('Unable to delete:', error);
			}
		}
	};

	/**
	 * Adds or updates a filter on the current listing.
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private addOrUpdateFilter = (selectedFilter: SelectedFilter) => {
		this.setState((prevState: ListingContainerState<any>) => {
			const items = [...prevState.filters.selectedFilters];
			const filterIndex = items.findIndex((item) => item.name === selectedFilter.name);

			// if unable to find filter, add to the list.
			if (filterIndex === -1) {
				return {
					filters: { ...prevState.filters, selectedFilters: [...items, selectedFilter] },
					savedFilters: { ...prevState.savedFilters, isEditMode: true },
				};
			}

			// if we find a filter, let's just update it.
			const itemToUpdate = items[filterIndex];
			itemToUpdate.value = selectedFilter.value;
			items[filterIndex] = itemToUpdate;

			return {
				filters: { ...prevState.filters, selectedFilters: items },
				savedFilters: { ...prevState.savedFilters },
			};
		}, this.executeSearch);
	};

	/**
	 * Removes a filter from the current listing
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private removeFilter = (selectedFilter: SelectedFilter) => {
		if (selectedFilter.permanent) {
			return;
		}

		this.setState((prevState: ListingContainerState<any>) => {
			return {
				filters: {
					...prevState.filters,
					selectedFilters: prevState.filters.selectedFilters.filter(
						(existingFilter) => existingFilter.name !== selectedFilter.name,
					),
				},
				savedFilters: {
					...prevState.savedFilters,
					isEditMode: prevState.filters.selectedFilters.length !== 1,
				},
			};
		}, this.executeSearch);
	};

	/**
	 * Toggles the display of the popover of available filter when clicking on "Filter"
	 *
	 * @memberof ListingContainer
	 */
	public toggleFilterList = () => {
		this.setState((prevState) => {
			return {
				filters: {
					...prevState.filters,
					displayFilterList: !prevState.filters.displayFilterList,
					filterBeingChanged: null,
				},
			};
		});
	};

	/**
	 * Closes the display of the popover of available filter
	 *
	 * @memberof ListingContainer
	 */
	public closeFilterList = () => {
		this.setState((prevState) => {
			return {
				filters: {
					...prevState.filters,
					displayFilterList: false,
				},
			};
		});
	};

	/**
	 * Toggles the edit mode of a specific filter
	 *
	 * @memberof ListingContainer
	 */
	public toggleFilterChange = () => {
		if (this.state.filters.filterBeingChanged !== null) {
			this.setState((prevState) => {
				return {
					filters: {
						...prevState.filters,
						filterBeingChanged: null,
					},
				};
			});
		}
	};

	/**
	 * Called when a filter is being added or edited. We store the filter definition with its value
	 * in state, which makes it trivial to modify the UI as necessary.
	 *
	 * @memberof ListingContainer
	 */
	public beginFilterChange = (selectedFilter: SelectedFilter, location: FilterChangeLocation) => {
		this.setState((prevState: ListingContainerState<any>) => {
			return {
				filters: {
					...prevState.filters,
					filterChangeLocation: location,
					filterBeingChanged: selectedFilter,
				},
			};
		});
	};

	/**
	 * Stops adding a new filter. Because we store the filter definition and value in state,
	 * it's trivial to stop this process and reset the UI.
	 *
	 * @memberof ListingContainer
	 */
	public cancelAddFilter = () => {
		this.setState((prevState: ListingContainerState<any>) => {
			return {
				filters: {
					...prevState.filters,
					filterBeingChanged: null,
				},
			};
		});
	};

	/**
	 * Stops editing a filter. Because we store the filter definition and value in state,
	 * it's trivial to stop this process and reset the UI.
	 *
	 * @memberof ListingContainer
	 */
	public cancelEditFilter = () => {
		this.setState((prevState) => {
			return {
				filters: {
					...prevState.filters,
					filterBeingChanged: null,
				},
			};
		});
	};

	/**
	 * Callback for when a filter is added/edited, so we can reset the UI state and then push state up to ListingSearch.
	 *
	 * @memberof ListingContainer
	 */
	public onFilterChange = (selectedFilter: SelectedFilter, stopEditing: boolean = true) => {
		if (stopEditing) {
			this.setState((prevState) => {
				return {
					filters: {
						...prevState.filters,
						filterBeingChanged: null,
						displayFilterList: false,
					},
				};
			});
		}

		this.addOrUpdateFilter(selectedFilter);
	};

	/**
	 * Callback for when a filter is removed, so we can reset the UI state and then push state up to ListingSearch.
	 *
	 * @private
	 * @memberof ListingContainer
	 */
	private onFilterRemoved = (selectedFilter: SelectedFilter, stopEditing: boolean = true) => {
		if (stopEditing) {
			this.setState((prevState) => {
				return {
					filters: {
						...prevState.filters,
						filterBeingChanged: null,
						displayFilterList: false,
					},
				};
			});
		}

		this.removeFilter(selectedFilter);
	};

	/**
	 * Handles updating order of items after a sort order change while in custom sort mode
	 */
	private onDragDropSortOrderUpdate = async (
		sourceIndex: number,
		destinationIndex: number | null,
		newSortOrder: number,
	) => {
		if (!this.state.results) {
			return;
		}

		// destinationIndex will be null when doing manual updates using the textbox
		if (destinationIndex !== null) {
			const newRecords = reorderArrayValues(this.state.results.records || [], sourceIndex, destinationIndex);

			this.setState((prevState: any) => {
				return {
					results: {
						...prevState.results,
						records: newRecords,
						uniqueId: uuid(), // ensure the wrapper component updates
					},
				};
			});
		}

		this.setState({
			isLoading: true,
		});

		const results = this.state.results;
		const record = results.records[sourceIndex];

		console.debug(
			'Moving:',
			record[this.props.entityDefinition.userFriendlyNameField],
			'to position',
			newSortOrder,
		);

		const sortOrderUpdateUrl = this.props.entityDefinition.baseAjaxRoute + '/sortorder';

		try {
			await AxiosHelper.post(sortOrderUpdateUrl, {
				id: record.id,
				sortOrder: newSortOrder,
			});
		} catch (error) {
			toastError({
				content: {
					message:
						'An error occurred while attempting to update sort order. Refreshing results. Please try again.',
					title: 'Sort Order Update Failed',
				},
			});
		}

		this.refreshData();
	};

	/**
	 * Updates the custom sorting mode. Resets all searches/filters and updates the current sorting
	 * field to be adequate to sort by.
	 */
	private setCustomSorting = (state: boolean) => {
		const isHierarchy = this.props.entityDefinition.isHierarchy;
		const parentFilter = isHierarchy && state ? this.props.entityDefinition.getRootParentSelectedFilter() : null;

		this.setState((prevState: any) => {
			return {
				customSorting: {
					...prevState.customSorting,
					isEnabled: state,
				},
				searchQuery: '',
				pendingSearchQuery: '',
				filters: { ...prevState.filters, selectedFilters: parentFilter ? [parentFilter] : [] },
				savedFilters: { ...prevState.savedFilters, appliedSavedFilter: null, isEditMode: false },
				results: { ...prevState.results, appliedSavedFilterId: '', page: 1 },
				sortBy: {
					title: 'Tree Sort Name',
					name: isHierarchy ? 'TreeSortString' : 'SortOrder',
					direction: SortByDirection.Ascending,
				},
			};
		}, this.executeSearch);
	};

	public render() {
		return (
			<>
				<Alert
					isOpen={this.state.isAlertOpen}
					onConfirm={this.closeAlert}
					renderTitle={() => 'Warning'}
					renderContents={() => this.state.alertMessage}
					color="warning"
				/>
				{this.state.pageChangeAlert ? (
					<VisuallyHiddenAnnouncementTrigger message={this.state.pageChangeAlert} />
				) : null}
				{this.props.children({
					...this.state,
					listingDisplayMode: this.props.listingDisplayMode ?? ListingDisplayMode.ListingPage,
				})}
			</>
		);
	}
}

// wrapped component type needed for tests
export class ListingContainerWrapped extends ListingContainer {}

export default withRouter<ListingContainerProps, any>(ListingContainerWrapped);
